import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  merchantWriteCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { merchantEndpoints } from "@/features/merchant/api/endpoints";
import type { MerchantDto } from "@/features/merchant/api/dto";

/**
 * Lie un portefeuille de reversement à un marchand
 * (`POST /Merchants/{id}/settlement-wallet?walletId=...`). Rôles :
 * flashpay-superadmin, flashpay-admin.
 */
export const useSetSettlementWallet = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["merchant", "merchants", "set-settlement-wallet"],
    mutationFn: ({
      merchantId,
      walletId,
    }: {
      merchantId: string;
      walletId: string;
    }) =>
      mutateData({
        url: `${merchantEndpoints.merchants.settlementWallet(merchantId)}?walletId=${walletId}`,
        method: "POST",
      }) as Promise<MerchantDto>,
    onSuccess: (_data, { merchantId }) => {
      return invalidateAfterMutation(
        queryClient,
        merchantWriteCacheKeys(merchantId)
      );
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useSetSettlementWallet failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "merchant" }
      );
    },
  });
};
